import { Alert, Box, Button, ButtonGroup, Grid, Modal, Paper, TextField, Typography } from "@mui/material";
import { IAirportGate, GateTypes } from "@vmosolutions/oc-interfaces";
import { useEffect, useMemo, useState } from "react";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import AddIcon from "@mui/icons-material/Add";
import { FormikSelectField, FormikTextField } from "src/components/MUI5FormikComponents/FormikWrappers";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";

interface IGateManagementProps {
  parentForm: any;
  showGateModal: boolean;
  setShowGateModal: any;
}

export default function GateManagement(props: IGateManagementProps): JSX.Element {
  const { parentForm, showGateModal, setShowGateModal } = props;
  const [activeGate, setActiveGate] = useState<IAirportGate | null>(null);
  const [gateFilter, setGateFilter] = useState<string>("");
  const [gateList, setGateList] = useState<IAirportGate[]>([]);
  const [pendingChanges, setPendingChanges] = useState<boolean>(false);
  type GateFormType = FormikProps<IAirportGate>;
  const defaultGateValues: IAirportGate = {
    code: "",
    terminal: undefined,
    type: GateTypes.Cargo,
    loc: {
      type: "Point",
      coordinates: [],
    },
  };

  useEffect(() => {
    setGateList(parentForm.values.gates);
  }, [parentForm.values.gates]);

  const initialValues = useMemo(() => {
    if (activeGate) {
      return activeGate;
    } else {
      return defaultGateValues;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGate]);

  const typeOptions = Object.keys(GateTypes).map((key) => ({
    label: key,
    value: GateTypes[key as keyof typeof GateTypes],
  }));

  const GateSchema = Yup.object().shape({
    code: Yup.string()
      .required("Code is required")
      .test("unique-code", "Code already in use", function (value: any) {
        if (activeGate && value === activeGate.code) {
          return true;
        } else {
          const existingIdx = gateList.findIndex((gate) => gate.code === value);
          return existingIdx === -1;
        }
      }),
    loc: Yup.object().shape({
      type: Yup.string().required("Location type is required"),
      coordinates: Yup.array(),
      "coordinates[0]": Yup.number().test(
        "testLong",
        "Longitude  must be between -180 and 180",
        function (value: number) {
          return (
            this.path === "loc.coordinates[0]" &&
            !!this.parent.coordinates[0] &&
            this.parent.coordinates[0] >= -180 &&
            this.parent.coordinates[0] <= 180
          );
        }
      ),
      "coordinates[1]": Yup.number().test("testLat", "Latitude must be between -90 and 90", function (value: number) {
        return (
          this.path === "loc.coordinates[1]" &&
          !!this.parent.coordinates[1] &&
          this.parent.coordinates[1] >= -90 &&
          this.parent.coordinates[1] <= 90
        );
      }),
    }),
  });

  const handleSubmit = (values: IAirportGate, helpers: FormikHelpers<IAirportGate>): void => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    if (!activeGate) {
      // Addition
      const newGateList = [...gateList];
      newGateList.push(values);
      setGateList(newGateList);
    } else {
      // Edit
      const gateListIdx = gateList.findIndex((gate) => gate.code === activeGate.code);
      const newGateList = [...gateList];
      newGateList[gateListIdx] = values;
      setActiveGate(values);
      setGateList(newGateList);
    }
    setSubmitting(false);
    resetForm();
    setStatus({ success: true });
    setPendingChanges(true);
    return;
  };

  const handleClose = (): void => {
    parentForm.setFieldValue("gates", gateList);
    setShowGateModal(false);
    setActiveGate(null);
    setPendingChanges(false);
  };

  return (
    <Modal open={showGateModal}>
      <Paper
        sx={{
          width: "60%",
          minWidth: "400px",
          p: 3,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {pendingChanges && (
          <Alert variant="filled" severity="warning">
            Gate changes will be saved when you click the close button
          </Alert>
        )}
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={GateSchema}
        >
          {(gateForm: GateFormType) => {
            const { dirty, isValid, isSubmitting, handleReset } = gateForm;
            return (
              <Form>
                <Grid container spacing={6} sx={{ pt: 2, display: "flex", alignItems: "start" }}>
                  <Grid container item xs={6}>
                    <Grid item xs={12}>
                      <TextField
                        placeholder="Filter by code or terminal"
                        variant="outlined"
                        size="small"
                        fullWidth
                        onChange={(e) => {
                          const val = e.target.value;
                          setGateFilter(val);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        color="success"
                        variant="contained"
                        size="small"
                        onClick={() => {
                          gateForm.resetForm();
                          setActiveGate(null);
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <AddIcon fontSize="small" /> Add New Gate
                      </Button>
                    </Grid>
                    <Grid item xs={12} sx={{ overflowY: "scroll", maxHeight: "440px", textOverflow: "hidden" }}>
                      {gateList
                        .filter((gate: IAirportGate) => {
                          const filter = gateFilter.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");

                          return (
                            gate.code.toLowerCase().includes(filter) || gate?.terminal?.toLowerCase().includes(filter)
                          );
                        })
                        .sort((a: IAirportGate, b: IAirportGate) => {
                          if (a.code < b.code) {
                            return -1;
                          } else {
                            return 1;
                          }
                        })
                        .map((gate: IAirportGate, index: number) => {
                          return (
                            <DraggableListItem
                              key={index}
                              active={activeGate && activeGate.code === gate.code}
                              onClick={() => setActiveGate(gate)}
                              sx={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
                            >
                              <Box>
                                {gate.code}
                                {gate.terminal && ` (Terminal ${gate.terminal})`}
                              </Box>
                              {!!gate.type && <Box>{GateTypes[gate.type]}</Box>}
                            </DraggableListItem>
                          );
                        })}
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6">{activeGate ? "Edit Gate" : "Add New Gate"}</Typography>
                    <FormikTextField name="code" label="Code" />
                    <FormikTextField name="terminal" label="Terminal" />
                    <FormikTextField
                      name="loc.coordinates[1]"
                      label="Latitude"
                      type="number"
                      placeholder="example: 44.0842165"
                    />
                    <FormikTextField
                      name="loc.coordinates[0]"
                      label="Longitude"
                      type="number"
                      placeholder="example: -63.5086112"
                    />
                    <FormikSelectField
                      name="type"
                      label="Type"
                      format={(type: string) => typeOptions.find((option: any) => option.value === type)}
                      parse={(option: any) => option.value}
                      options={typeOptions}
                      isClearable={false}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ pt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <ButtonGroup size="small" variant="contained">
                      <Button color="success" type="submit" disabled={isSubmitting || !dirty || !isValid}>
                        Save
                      </Button>
                      <Button onClick={() => handleClose()}>Close</Button>
                    </ButtonGroup>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </Modal>
  );
}
